import React, {Component} from 'react';
import Wrapper from "../Wrapper";
import axios, {CancelTokenSource} from "axios";
import {Indicators} from "../../classes/indicators";

const filterFields = [
    {key: 'short_name', label: 'Country', allLabel: 'All countries'},
    {key: 'source_origin', label: 'Source origin', allLabel: 'All sources'},
    {key: 'logical_framework_component', label: 'Logical framework component', allLabel: 'All components'},
    {key: 'actors', label: 'Actors', allLabel: 'All actors'},
] as const;

type FilterKey = typeof filterFields[number]['key'];
type Filters = Record<FilterKey, string>;
type State = {
    indicators: Indicators[];
    filters: Filters;
    page: number;
    loading: boolean;
    error: string;
};

const emptyFilters = (): Filters => ({
    short_name: '',
    source_origin: '',
    logical_framework_component: '',
    actors: '',
});
const pageSize = 10;
const requestPageSize = 1000;

class Scoreboard extends Component<{}, State> {
    state: State = {
        indicators: [],
        filters: emptyFilters(),
        page: 1,
        loading: true,
        error: '',
    };

    cancelSource?: CancelTokenSource;

    componentDidMount() {
        this.loadIndicators();
    }

    componentWillUnmount() {
        this.cancelSource?.cancel();
    }

    loadIndicators = async () => {
        this.cancelSource?.cancel();
        const cancelSource = axios.CancelToken.source();
        this.cancelSource = cancelSource;
        this.setState({loading: true, error: ''});

        try {
            const first = await axios.get('scoreboard/', {
                params: {page: 1, page_size: requestPageSize},
                cancelToken: cancelSource.token,
            });
            const indicators: Indicators[] = [...first.data.data];

            // This API's last_page field contains the record count, not the page count.
            const totalPages = Math.ceil(first.data.meta.last_page / first.data.meta.page_size);
            for (let page = 2; page <= totalPages; page++) {
                const response = await axios.get('scoreboard/', {
                    params: {page, page_size: requestPageSize},
                    cancelToken: cancelSource.token,
                });
                indicators.push(...response.data.data);
            }

            this.setState({indicators, page: 1, loading: false});
        } catch (error) {
            if (axios.isCancel(error)) return;
            this.setState({loading: false, error: 'Unable to load the scoreboard. Please try again.'});
        }
    };

    handleFilterChange = (key: FilterKey, value: string) => {
        this.setState(state => ({filters: {...state.filters, [key]: value}, page: 1}));
    };

    render() {
        const {indicators, filters, page, loading, error} = this.state;
        const filteredIndicators = indicators.filter(indicator =>
            filterFields.every(({key}) => !filters[key] || (indicator[key] || '').trim() === filters[key])
        );
        const totalPages = Math.max(1, Math.ceil(filteredIndicators.length / pageSize));
        const visibleIndicators = filteredIndicators.slice((page - 1) * pageSize, page * pageSize);
        const hasFilters = filterFields.some(({key}) => Boolean(filters[key]));

        return (
            <Wrapper>
                <h2 className="mt-3">Scoreboard</h2>

                <div className="row g-3 mb-3" role="group" aria-label="Scoreboard filters">
                    {filterFields.map(({key, label, allLabel}) => {
                        const options = Array.from(new Set(indicators.map(indicator =>
                            (indicator[key] || '').trim()
                        ).filter(Boolean))).sort((a, b) => a.localeCompare(b));

                        return (
                            <div className="col-12 col-md-6 col-xl-3" key={key}>
                                <label htmlFor={`scoreboard-${key}`} className="form-label">{label}</label>
                                <select
                                    id={`scoreboard-${key}`}
                                    className="form-select"
                                    value={filters[key]}
                                    disabled={loading || Boolean(error)}
                                    onChange={event => this.handleFilterChange(key, event.target.value)}
                                >
                                    <option value="">{allLabel}</option>
                                    {options.map(option => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </div>
                        );
                    })}
                </div>

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div role="status" aria-live="polite">
                        {loading ? 'Loading scoreboard…' : !error && (
                            <>
                                {filteredIndicators.length === 0 ? '0' : (page - 1) * pageSize + 1}
                                {'–'}{Math.min(page * pageSize, filteredIndicators.length)}
                                {' of '}{filteredIndicators.length.toLocaleString()} results
                                {hasFilters && ` (filtered from ${indicators.length.toLocaleString()})`}
                            </>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={loading || !hasFilters}
                        onClick={() => this.setState({filters: emptyFilters(), page: 1})}
                    >
                        Clear filters
                    </button>
                </div>

                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}{' '}
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={this.loadIndicators}>
                            Retry
                        </button>
                    </div>
                )}

                <div className="table-responsive" aria-busy={loading}>
                    <table className="table table-striped table-sm">
                        <thead>
                        <tr>
                            <th>#</th>
                            <th>SHORT_NAME</th>
                            <th>INDICATORSOURCEID</th>
                            <th>INDICATORNAME</th>
                            <th>SOURCE_ORIGIN</th>
                            <th>VALUE</th>
                            <th>LATEST_YEAR</th>
                            <th>RANKING</th>
                            <th>LOGICAL_FRAMEWORK_COMPONENT</th>
                            <th>ACTORS</th>
                            <th>STI_ACTIVITIES_MAIN_SUBJECTS</th>
                        </tr>
                        </thead>
                        <tbody>
                        {!loading && !error && visibleIndicators.map(
                            (indicators: Indicators) => {
                                return (
                                    <tr key={indicators.id}>
                                        <td>{indicators.id}</td>
                                        <td>{indicators.short_name}</td>
                                        <td>{indicators.indicatorsourceid}</td>
                                        <td>{indicators.indicatorname}</td>
                                        <td>{indicators.source_origin}</td>
                                        <td>{indicators.value}</td>
                                        <td>{indicators.latest_year}</td>
                                        <td>{indicators.ranking}</td>
                                        <td>{indicators.logical_framework_component}</td>
                                        <td>{indicators.actors}</td>
                                        <td>{indicators.sti_activities_main_subjects}</td>
                                    </tr>
                                )
                            }
                        )}
                        {!loading && !error && filteredIndicators.length === 0 && (
                            <tr>
                                <td colSpan={11} className="text-center py-4">
                                    No indicators match these filters. Change a filter or clear filters to see more results.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <nav className="d-flex align-items-center gap-3 mb-3" aria-label="Scoreboard pagination">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={loading || Boolean(error) || page === 1}
                        onClick={() => this.setState(state => ({page: state.page - 1}))}
                    >
                        Previous
                    </button>
                    <span>Page {page} of {totalPages}</span>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={loading || Boolean(error) || page >= totalPages}
                        onClick={() => this.setState(state => ({page: state.page + 1}))}
                    >
                        Next
                    </button>
                </nav>

            </Wrapper>
        )
    }
}

export default Scoreboard;
